import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
    Smartphone,
    CheckCircle2,
    Phone,
    Copy,
    Check,
    Loader2,
    Hash,
    MessageCircle,
    AlertCircle,
    Users
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { FacebookIcon } from '@/components/icons/FacebookIcon';
import { useState } from 'react';
import type { Agent } from '@/api/tenants/tenants.types';

const itemVariants = {
    hidden: {
        opacity: 0,
        y: 10
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            duration: 0.3
        }
    }
};

export const MessagingAccountSkeleton = () => (
    <Card className="shadow-sm">
        <div className="p-5 space-y-4">
            {/* Header */}
            <div className="flex items-start gap-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <Skeleton className="h-5 w-2/3" />
                        <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                    <div className="flex items-center gap-3">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-3 w-16" />
                    </div>
                </div>
            </div>

            {/* Key Parameters */}
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border bg-muted/50 p-3">
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4" />
                        <div className="flex-1 space-y-1">
                            <Skeleton className="h-3 w-12" />
                            <Skeleton className="h-4 w-8" />
                        </div>
                    </div>
                </div>
                <div className="rounded-lg border bg-muted/50 p-3">
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4" />
                        <div className="flex-1 space-y-1">
                            <Skeleton className="h-3 w-14" />
                            <Skeleton className="h-4 w-16" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
                <Skeleton className="h-9 flex-1" />
                <Skeleton className="h-9 flex-1" />
            </div>
        </div>
    </Card>
);

interface MessagingAccountCardProps {
    agent: Agent;
    type: 'whatsapp' | 'facebook';
    onCopyDetails: (value: string, message: string) => void;
}

export const MessagingAccountCard = ({ 
    agent, 
    type, 
    onCopyDetails
}: MessagingAccountCardProps) => {
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const handleCopy = (value: string, message: string, fieldType: string) => {
        onCopyDetails(value, message);
        setCopiedField(fieldType);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const getStatusBadge = () => {
        if (agent.in_sync) {
            return {
                icon: <CheckCircle2 className="w-3 h-3" />,
                label: "Connected",
                className: type === 'whatsapp'
                    ? "bg-green-500/10 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-700"
                    : "bg-blue-500/10 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700"
            };
        } else {
            return {
                icon: <AlertCircle className="w-3 h-3" />,
                label: "Disconnected", 
                className: "bg-red-500/10 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-700"
            };
        }
    };

    const status = getStatusBadge();

    return (
        <motion.div variants={itemVariants}>
            <Card className="shadow-sm hover:shadow-md transition-shadow">
                {/* Main Content */}
                <div className="p-5 space-y-4">
                    {/* Header with Icon and Title */}
                    <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={cn(
                            "flex h-10 w-10 items-center justify-center rounded-lg",
                            type === 'whatsapp' 
                                ? 'bg-green-500/10 dark:bg-green-900/30' 
                                : 'bg-blue-500/10 dark:bg-blue-900/30'
                        )}>
                            {type === 'whatsapp' ? (
                                <WhatsAppIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                            ) : (
                                <FacebookIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            )}
                        </div>

                        {/* Title and Status */}
                        <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                                <h3 className="text-base font-semibold tracking-tight truncate">
                                    {agent.name}
                                </h3>
                                <div
                                    className={cn(
                                        "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border shrink-0",
                                        status.className
                                    )}
                                >
                                    {status.icon}
                                    <span>{status.label}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1">
                                    <Phone className="h-3.5 w-3.5" />
                                    <span className="font-mono">{agent.display_id}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Hash className="h-3.5 w-3.5" />
                                    <span>ID: {agent.id}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Key Parameters */}
                    <div className="grid grid-cols-2 gap-3">
                        {/* Unread Conversations */}
                        <div className="rounded-lg border bg-muted/50 p-3">
                            <div className="flex items-center gap-2">
                                <MessageCircle className={cn("h-4 w-4", type === 'whatsapp' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400')} />
                                <div className="flex-1">
                                    <div className="text-xs text-muted-foreground">Unread</div>
                                    <div className="text-sm font-semibold">
                                        {agent.unread_conversations_count > 0 ? (
                                            <span className="text-orange-600 dark:text-orange-400">
                                                {agent.unread_conversations_count > 99 ? '99+' : agent.unread_conversations_count}
                                            </span>
                                        ) : (
                                            '0'
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Channel Type */}
                        <div className="rounded-lg border bg-muted/50 p-3">
                            <div className="flex items-center gap-2">
                                <Users className={cn("h-4 w-4", type === 'whatsapp' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400')} />
                                <div className="flex-1">
                                    <div className="text-xs text-muted-foreground">Channel</div>
                                    <div className="text-sm font-semibold capitalize">
                                        {agent.channel}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Copy Actions */}
                    <div className="flex gap-2">
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(agent.display_id, `${type === 'whatsapp' ? 'Phone number' : 'Page ID'} copied!`, 'display_id');
                            }}
                            variant="outline"
                            size="sm"
                            className="flex-1 gap-2"
                        >
                            {copiedField === 'display_id' ? (
                                <Check className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                                <Copy className="h-3.5 w-3.5" />
                            )}
                            {type === 'whatsapp' ? 'Copy Phone' : 'Copy Page ID'}
                        </Button>

                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(agent.id.toString(), 'Agent ID copied!', 'agent_id');
                            }}
                            variant="outline"
                            size="sm"
                            className="flex-1 gap-2"
                        >
                            {copiedField === 'agent_id' ? (
                                <Check className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                                <Copy className="h-3.5 w-3.5" />
                            )}
                            Copy ID
                        </Button>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};

// Empty state card for adding new accounts
interface AddAccountCardProps {
    type: 'whatsapp' | 'facebook';
    onAddAccount: (type: 'whatsapp' | 'facebook') => void;
    disabled?: boolean;
    isLoading?: boolean;
}

export const AddAccountCard = ({ type, onAddAccount, disabled = false, isLoading = false }: AddAccountCardProps) => {
    const getChannelIcon = () => {
        if (type === 'whatsapp') {
            return <WhatsAppIcon className="w-6 h-6 text-white" />;
        }
        return <FacebookIcon className="w-6 h-6 text-white" />;
    };

    const getChannelName = () => {
        return type === 'whatsapp' ? 'WhatsApp Business' : 'Facebook Messenger';
    };

    const getChannelColor = () => {
        if (disabled) return 'from-gray-400 to-gray-500';
        return type === 'whatsapp' 
            ? 'from-green-500 to-green-600' 
            : 'from-blue-500 to-blue-600';
    };

    return (
        <motion.div variants={itemVariants}>
            <Card className={cn(
                "group relative overflow-hidden transition-all duration-300 cursor-pointer border-dashed",
                disabled 
                    ? "opacity-50 cursor-not-allowed" 
                    : "hover:-translate-y-1 hover:shadow-xl"
            )}>
                <div className="relative p-5 space-y-4">
                    <div className="flex flex-col items-center text-center space-y-4">
                        {/* Icon */}
                        <div className={cn(
                            "h-12 w-12 rounded-xl bg-gradient-to-br flex items-center justify-center shadow-lg",
                            getChannelColor()
                        )}>
                            {getChannelIcon()}
                        </div>

                        {/* Content */}
                        <div className="space-y-2">
                            <h3 className="text-lg font-semibold tracking-tight">
                                Add {getChannelName()}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                {disabled 
                                    ? "Coming soon" 
                                    : `Connect your ${getChannelName()} account`
                                }
                            </p>
                        </div>

                        {/* Add Button */}
                        <Button
                            onClick={() => !disabled && onAddAccount(type)}
                            disabled={disabled || isLoading}
                            variant="outline"
                            size="sm"
                            className="mt-4"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    {type === 'whatsapp' ? (
                                        <WhatsAppIcon className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Smartphone className="h-4 w-4 mr-2" />
                                    )}
                                    {disabled ? 'Coming Soon' : 'Connect Account'}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};
